// Utilities to apply appearance settings to the document root.
// Copied minimal logic from AppearanceModal so it can be used globally.
// NOTE: Font selection has been centralized in fontGovernance.ts (Titillium Web only)

import { applyCanonicalFont } from './fontGovernance';

export type Appearance = {
  palette: string;
  // NOTE: 'font' property has been deprecated and moved to fontGovernance.ts
  // The app now uses Titillium Web exclusively
  customPrimary?: string;
  customAccent?: string;
  customForeground?: string;
  theme?: 'light' | 'dark' | 'auto';
  darkNotch?: boolean;
};

function hexToHslString(hex: string) {
  const h = hex.replace('#', '');
  const bigint = parseInt(h, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  const rNorm = r / 255;
  const gNorm = g / 255;
  const bNorm = b / 255;
  const max = Math.max(rNorm, gNorm, bNorm);
  const min = Math.min(rNorm, gNorm, bNorm);
  let hDeg = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rNorm:
        hDeg = (gNorm - bNorm) / d + (gNorm < bNorm ? 6 : 0);
        break;
      case gNorm:
        hDeg = (bNorm - rNorm) / d + 2;
        break;
      case bNorm:
        hDeg = (rNorm - gNorm) / d + 4;
        break;
    }
    hDeg = hDeg * 60;
  }

  return `${Math.round(hDeg)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function hexToRgb(hex: string) {
  const h = hex.replace('#', '');
  const bigint = parseInt(h, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return { r, g, b };
}

// Minimal apply: supports custom palettes (customPrimary/customAccent/customForeground)
// OPTIMIZED: Defers heavy calculations to avoid blocking initial render
// Font application is now handled by fontGovernance.ts
export function applyAppearanceToRoot(settings: Appearance) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const localSettings = { ...settings };
  
  // Apply canonical font (Titillium Web)
  applyCanonicalFont();
  
  // Only mutate dark/light mode when theme is explicit.
  // If theme is omitted (common in collaborative color updates), preserve current mode.
  if (localSettings.theme === 'dark') {
    root.classList.add('dark');
  } else if (localSettings.theme === 'light') {
    root.classList.remove('dark');
  }
  
  try {
    // FAST PATH: Apply primary colors immediately (simple assignment)
    if (localSettings.palette === 'custom' && localSettings.customPrimary) {
      // Quick HSL conversion - fast approximation
      const primary = hexToHslString(localSettings.customPrimary);
      const accent = localSettings.customAccent ? hexToHslString(localSettings.customAccent) : primary;
      
      root.style.setProperty('--primary', primary);
      root.style.setProperty('--accent', accent);
      
      // DEFERRED: Calculate hover/active variants on idle
      // This prevents blocking the main render thread
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => {
          try {
            function adjustLightness(hslStr: string, delta: number) {
              const m = hslStr.match(/(\d+)\s+(\d+)%\s+(\d+)%/);
              if (!m) return hslStr;
              const h = m[1];
              const s = m[2];
              let l = parseInt(m[3], 10);
              l = Math.max(0, Math.min(100, l + delta));
              return `${h} ${s}% ${l}%`;
            }
            
            root.style.setProperty('--primary-hover', adjustLightness(primary, -8));
            root.style.setProperty('--primary-active', adjustLightness(primary, -12));
            root.style.setProperty('--accent-hover', adjustLightness(accent, -8));
            root.style.setProperty('--accent-active', adjustLightness(accent, -12));
            root.style.setProperty('--destructive-hover', adjustLightness('0 70% 55%', -8));
            root.style.setProperty('--destructive-active', adjustLightness('0 70% 55%', -12));
            root.style.setProperty('--secondary-hover', adjustLightness('240 10% 90%', -6));
            root.style.setProperty('--secondary-active', adjustLightness('240 10% 90%', -10));
            root.style.setProperty('--muted-hover', adjustLightness('240 15% 95%', -4));
            
            // Derived accent-foreground on idle
            try {
              const accentHex = localSettings.customAccent || localSettings.customPrimary;
              if (accentHex) {
                const { r: ar, g: ag, b: ab } = hexToRgb(accentHex);
                const ars = ar / 255, ags = ag / 255, abs = ab / 255;
                const asrgb = [ars, ags, abs].map((c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)));
                const alum = 0.2126 * asrgb[0] + 0.7152 * asrgb[1] + 0.0722 * asrgb[2];
                if (alum > 0.5) {
                  root.style.setProperty('--accent-foreground', '240 20% 10%');
                } else {
                  root.style.setProperty('--accent-foreground', '240 20% 90%');
                }
              }
            } catch (e) {}
          } catch (e) {}
        }, { timeout: 2000 });
      }
    }

    // Font application is now governed by fontGovernance.ts (always Titillium Web)
    
    if (localSettings.palette && localSettings.palette !== 'custom') {
      // Non-custom palettes: assign preset values
      if (localSettings.primary) root.style.setProperty('--primary', localSettings.primary);
      if (localSettings.accent) root.style.setProperty('--accent', localSettings.accent);
    }

    // customForeground override
    if (settings.customForeground) {
      try { 
        root.style.setProperty('--foreground', hexToHslString(settings.customForeground)); 
      } catch (e) {}
    }
  } catch (e) {
    // swallow
    if (process.env.NODE_ENV === 'development') {
      console.warn('[appearance] apply failed', e);
    }
  }
  
  // Notify that appearance has been updated
  window.dispatchEvent(new CustomEvent('appearance-updated'));
}

export default applyAppearanceToRoot;
